import { Table, TableColumnsType } from 'antd';
import { FC, ReactNode, useMemo } from 'react';
import { ICacUsageMetricValues } from '../../../models/statistics.model';

export interface ICacReportTableRow {
  key: string;
  label: string;
  metrics: Partial<ICacUsageMetricValues>;
}

interface IProps {
  rowHeaderLabel: string;
  rows: ICacReportTableRow[];
  loading?: boolean;
}

const fmtNum = (n: number | null | undefined, suffix: string) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n.toLocaleString('ko-KR')} ${suffix}`;
};

const fmtPct = (n: number | null | undefined) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n.toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

/** 헤더는 컬럼 기본(center), 본문만 우측 — antd column `align`은 th/td 함께 바뀌므로 */
const AlignRightWrap: FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ textAlign: 'right' }}>{children}</div>
);

const CacReportTable: FC<IProps> = ({
  rowHeaderLabel,
  rows,
  loading,
}) => {
  const columns: TableColumnsType<ICacReportTableRow> = useMemo(
    () => [
      {
        title: rowHeaderLabel,
        dataIndex: 'label',
        key: 'label',
        width: 160,
      },
      {
        title: '전체 학생수',
        key: 'enrolledCount',
        render: (_, row) => (
          <AlignRightWrap>
            {fmtNum(row.metrics.enrolledCount, '명')}
          </AlignRightWrap>
        ),
      },
      {
        title: '총 조회수',
        key: 'totalViews',
        render: (_, row) => (
          <AlignRightWrap>{fmtNum(row.metrics.totalViews, '건')}</AlignRightWrap>
        ),
      },
      {
        title: '사용률',
        key: 'usageRate',
        render: (_, row) => (
          <AlignRightWrap>{fmtPct(row.metrics.usageRate)}</AlignRightWrap>
        ),
      },
      {
        title: '학생 고유 조회수',
        key: 'uniqueStudentViews',
        render: (_, row) => (
          <AlignRightWrap>
            {fmtNum(row.metrics.uniqueStudentViews, '명')}
          </AlignRightWrap>
        ),
      },
      {
        title: '학생 고유 사용률',
        key: 'uniqueStudentRate',
        render: (_, row) => (
          <AlignRightWrap>
            {fmtPct(row.metrics.uniqueStudentRate)}
          </AlignRightWrap>
        ),
      },
      {
        title: '담임 고유 조회수',
        key: 'homeroomUniqueViews',
        render: (_, row) => (
          <AlignRightWrap>
            {fmtNum(row.metrics.homeroomUniqueViews, '명')}
          </AlignRightWrap>
        ),
      },
      {
        title: '담임 고유 사용률',
        key: 'homeroomUniqueRate',
        render: (_, row) => (
          <AlignRightWrap>
            {fmtPct(row.metrics.homeroomUniqueRate)}
          </AlignRightWrap>
        ),
      },
    ],
    [rowHeaderLabel],
  );

  return (
    <div className="comm-table__area" style={{ overflowX: 'auto' }}>
      <Table<ICacReportTableRow>
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
      />
    </div>
  );
};

export default CacReportTable;
